import type { MessageKey } from '@bookingdei/i18n/message-key';
import type { PlanEntitlements } from '../mock/seeds/schema-types';
import type { NavIconName } from './nav-icon';

// The merchant dashboard navigation model (PRD v2.5 §7.3.3, DR-37, DR-45; design
// navItems() M:8393-8420). One named list (rule 7.1-2 bans utils/) read by the
// glass rail, the top bar's page title, the 4-slot mobile bar and the More sheet, so
// they cannot drift.
//
//   group    rail section: Work · Grow · Account; `more` items (Inbox, QR studio,
//            DR-37) are not in the design rail — they live in the More sheet on
//            mobile and in a fourth rail group on desktop.
//   slot     one of the three page slots of the mobile bar (the fourth is More).
//   badge    'pending_proofs' → the live payment-verification count (FR-B11a).
//   entitlement  a boolean plan entitlement (DB-53a) that must be true for the
//            page; when false the item shows a lock glyph and the page renders the
//            upgrade state (FR-B22 — locks render, never hide).
//   deferred the page's content is deferred (Promote, DR-32): nav entry only, with a
//            "Soon" chip and a coming-later page.
//   staff    visible in a staff session (active_role = staff); owners see all.
//            The design's team member (`access()`, persona `xh-team`) sees Home ·
//            Bookings · Services; Services is not offered here because its catalog
//            read is merchant-only under API-16 (no staff-safe read in the contract),
//            and Inbox (merchant threads) is merchant-only too.
export type NavGroup = 'work' | 'grow' | 'account' | 'more';

export interface NavItem {
  to: string;
  key: MessageKey;
  icon: NavIconName;
  group: NavGroup;
  slot?: boolean;
  badge?: 'pending_proofs';
  entitlement?: 'insights' | 'promote' | 'payments';
  deferred?: boolean;
  staff?: boolean;
}

export const navItems: readonly NavItem[] = [
  { to: '/', key: 'dc70.nav.home', icon: 'home', group: 'work', slot: true, staff: true },
  {
    to: '/bookings',
    key: 'dc70.nav.bookings',
    icon: 'calendar',
    group: 'work',
    slot: true,
    badge: 'pending_proofs',
    staff: true,
  },
  {
    to: '/services',
    key: 'dc70.nav.services',
    icon: 'layers',
    group: 'work',
    slot: true,
  },
  { to: '/business', key: 'dc70.nav.business', icon: 'store', group: 'work' },

  { to: '/offers', key: 'dc70.nav.offers', icon: 'tag', group: 'grow' },
  { to: '/promote', key: 'dc70.nav.promote', icon: 'megaphone', group: 'grow', deferred: true },
  {
    to: '/insights',
    key: 'dc70.nav.insights',
    icon: 'chart',
    group: 'grow',
    entitlement: 'insights',
  },

  { to: '/team', key: 'dc70.nav.team', icon: 'users', group: 'account' },
  { to: '/plan', key: 'dc70.nav.plan', icon: 'taka', group: 'account' },
  { to: '/account', key: 'dc70.nav.account', icon: 'person', group: 'account' },

  { to: '/inbox', key: 'dc70.nav.inbox', icon: 'inbox', group: 'more' },
  { to: '/qr', key: 'dc70.nav.qr', icon: 'qr', group: 'more' },
];

export const navGroups: readonly { group: NavGroup; label: MessageKey }[] = [
  { group: 'work', label: 'dc70.group.work' },
  { group: 'grow', label: 'dc70.group.grow' },
  { group: 'account', label: 'dc70.group.account' },
  { group: 'more', label: 'dc70.group.more' },
];

/** Items a session may see: owners see everything, staff only `staff` items. */
export function visibleNavItems(isStaff: boolean): NavItem[] {
  return navItems.filter((i) => !isStaff || i.staff);
}

/** The nav item a pathname belongs to (longest matching prefix), if any. */
export function navItemFor(pathname: string): NavItem | undefined {
  if (pathname === '/') return navItems[0];
  return navItems
    .filter((i) => i.to !== '/' && (pathname === i.to || pathname.startsWith(`${i.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0];
}

/**
 * Whether the plan locks this item (FR-B22). Unknown entitlements (subscription not
 * loaded yet) never lock — the page itself re-checks once the plan arrives.
 */
export function isLocked(item: NavItem, entitlements: PlanEntitlements | undefined): boolean {
  if (!item.entitlement || !entitlements) return false;
  return entitlements[item.entitlement] === false;
}
